import React, { useContext, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Landmark, Loader2, CheckCircle2, AlertCircle, ShieldCheck } from "lucide-react";
import { BoxWrapper } from "../../../style";
import { FormWrapper2 } from "../../../components/loan/style";
import Button from "../../../components/ui/Button";
import Alert from "../../../components/ui/Alert";
import { accountAggregator } from "../../../Utils/api";
import { getStorage, setStorage } from "../../../Utils/common";
import ContextDashboard from "../../../Context/ContextDashboard";

// The bank statement is shared through Ignosis Account Aggregator. The
// customer is sent to the Ignosis journey and comes back to this page with
// ?trackingId=...&status=..., after which the backend fetches the statement.
// Manual upload is offered only when the backend allows it (fallback_allowed).

const POLL_INTERVAL_MS = 5000;
const POLL_LIMIT = 24; // about 2 minutes

function AccountAggregator() {
  const [loading, setLoading] = useState(false);
  const [checking, setChecking] = useState(true);
  const [aaStatus, setAaStatus] = useState("NOT_STARTED");
  const [fallbackAllowed, setFallbackAllowed] = useState(0);
  const [statusMessage, setStatusMessage] = useState("");
  const [message, setMessage] = useState("");
  const [timedOut, setTimedOut] = useState(false);
  const pollCount = useRef(0);
  const pollTimer = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();
  const { logout, handleEvent } = useContext(ContextDashboard);

  const goToNextStep = (data) => {
    setStorage("next_step", data?.next_step);
    setStorage("percent", data?.step_percentage);
    handleEvent(data?.next_step);
  };

  const verify = (trackingId, keepPolling) => {
    const params = {
      profile_id: getStorage("cust_profile_id") || "",
      event_name: "account_aggregator_verify",
      tracking_id: trackingId || getStorage("aa_tracking_id") || "",
    };

    accountAggregator(params).then((resp) => {
      setChecking(false);

      if (resp?.data?.Status === 4) {
        logout();
        return;
      }

      if (resp?.data?.Status !== 1) {
        setMessage({ type: "error", msg: resp?.data?.Message || "Unable to check the status. Please try again." });
        return;
      }

      const data = resp?.data?.Data || {};
      setAaStatus(data.aa_status);
      setFallbackAllowed(data.fallback_allowed);
      setStatusMessage(resp?.data?.Message || "");

      if (data.aa_status === "COMPLETED") {
        sessionStorage.removeItem("aa_tracking_id");
        setMessage({ type: "success", msg: "Bank statement received successfully", place: "global" });
        goToNextStep(data);
        return;
      }

      const stillWaiting = data.aa_status === "PROCESSING" || (keepPolling && data.aa_status === "PENDING");

      if (stillWaiting && pollCount.current < POLL_LIMIT) {
        pollCount.current += 1;
        pollTimer.current = setTimeout(() => verify(trackingId, keepPolling), POLL_INTERVAL_MS);
      } else if (stillWaiting) {
        setTimedOut(true);
      }
    });
  };

  useEffect(() => {
    const query = new URLSearchParams(location.search);
    const returnedTrackingId = query.get("trackingId");
    const returnedStatus = query.get("status");

    if (returnedTrackingId) {
      // Drop Ignosis' query params so a refresh does not re-read them.
      navigate("/my-dashboard/account-aggregator", { replace: true });
    }

    // Approved on the Ignosis side: the statement fetch takes a few seconds, keep polling.
    verify(returnedTrackingId, returnedStatus === "APPROVED");

    return () => clearTimeout(pollTimer.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const startAccountAggregator = () => {
    const params = {
      profile_id: getStorage("cust_profile_id") || "",
      event_name: "account_aggregator",
      // The login is kept per origin (www vs non-www); come back to this one.
      return_origin: window.location.origin,
    };

    setLoading(true);
    accountAggregator(params).then((resp) => {
      if (resp?.data?.Status === 4) {
        logout();
        return;
      }

      const data = resp?.data?.Data || {};

      if (resp?.data?.Status === 1 && data.redirection_url) {
        setStorage("aa_tracking_id", data.tracking_id);
        window.location.href = data.redirection_url;
        return;
      }

      setLoading(false);
      setMessage({ type: "error", msg: resp?.data?.Message || "Unable to connect to your bank. Please try again." });
    });
  };

  const checkAgain = () => {
    pollCount.current = 0;
    setTimedOut(false);
    setChecking(true);
    verify(null, false);
  };

  const uploadManually = () => {
    setStorage("next_step", "bank_statement_upload");
    handleEvent("bank_statement_upload");
  };

  const isWaiting = aaStatus === "PROCESSING" && !timedOut;

  return (
    <BoxWrapper className="w-full min-h-screen p-3 sm:p-5 md:p-8">
      <div className="w-full max-w-3xl mx-auto bg-white rounded-2xl md:rounded-3xl shadow-lg p-4 sm:p-6 md:p-10">
        <div className="flex gap-4 items-start">
          <div className="w-12 h-12 sm:w-14 sm:h-14 bg-[#EAF2F6] rounded-full flex items-center justify-center shrink-0">
            <Landmark className="text-[#2D5F7A]" />
          </div>
          <div className="flex-1">
            <h2 className="text-2xl sm:text-3xl font-bold text-[#1D3557]">Share Your Bank Statement</h2>
            <p className="text-gray-500 mt-2 text-sm sm:text-base">
              Securely share your last 6 months' bank statement directly from your bank through the RBI-regulated Account Aggregator. No PDF upload needed.
            </p>
          </div>
        </div>

        <FormWrapper2 className="mt-8">
          <Alert setMessage={setMessage} message={message} />

          {checking ? (
            <div className="flex items-center justify-center gap-3 py-12 text-gray-500">
              <Loader2 className="animate-spin" size={22} />
              <span>Checking status...</span>
            </div>
          ) : (
            <>
              {aaStatus === "NOT_STARTED" && (
                <ol className="space-y-3 text-sm sm:text-base text-gray-600">
                  {[
                    "Verify with an OTP sent to your registered mobile number.",
                    "Select the bank account your salary is credited to.",
                    "Approve the consent. Your statement is shared read-only.",
                  ].map((step, index) => (
                    <li key={step} className="flex gap-3 items-start">
                      <span className="w-6 h-6 rounded-full bg-[#EAF2F6] text-[#2D5F7A] text-xs font-semibold flex items-center justify-center shrink-0 mt-0.5">{index + 1}</span>
                      <span>{step}</span>
                    </li>
                  ))}
                </ol>
              )}

              {aaStatus === "PROCESSING" && (
                <div className="rounded-2xl border border-blue-200 bg-blue-50 p-5 sm:p-6">
                  <div className="flex gap-4 items-start">
                    {isWaiting ? (
                      <Loader2 className="text-[#2D5F7A] animate-spin shrink-0" size={28} />
                    ) : (
                      <AlertCircle className="text-[#2D5F7A] shrink-0" size={28} />
                    )}
                    <div>
                      <h4 className="font-semibold text-base sm:text-lg text-[#1D3557]">
                        {isWaiting ? "Fetching your bank statement" : "Still waiting for your bank"}
                      </h4>
                      <p className="text-sm sm:text-base text-gray-600 mt-1">
                        {isWaiting
                          ? "Consent approved. Your bank is sharing the statement with us - this usually takes less than a minute. Please keep this page open."
                          : "Your bank is taking longer than usual. You can check again, or come back later - we will pick it up automatically."}
                      </p>
                    </div>
                  </div>
                  <div className="mt-5 grid grid-cols-3 gap-2 text-xs sm:text-sm">
                    {[
                      ["Consent approved", true],
                      ["Fetching from bank", isWaiting],
                      ["Statement received", false],
                    ].map(([label, active], index) => (
                      <div key={label} className="flex items-center gap-2 text-gray-600">
                        {index === 0 ? (
                          <CheckCircle2 className="text-green-600 shrink-0" size={16} />
                        ) : (
                          <span className={`w-4 h-4 rounded-full shrink-0 border-2 ${active ? "border-[#2D5F7A] animate-pulse" : "border-gray-300"}`} />
                        )}
                        <span className={index === 0 ? "text-[#1D3557] font-medium" : ""}>{label}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {aaStatus === "PENDING" && (
                <div className="rounded-2xl border border-amber-200 bg-amber-50 p-5 flex gap-4 items-start">
                  <AlertCircle className="text-amber-600 shrink-0" size={28} />
                  <div>
                    <h4 className="font-semibold text-base sm:text-lg text-[#1D3557]">Consent not completed</h4>
                    <p className="text-sm sm:text-base text-gray-600 mt-1">{statusMessage || "The consent was not approved yet. Connect your bank account to continue."}</p>
                  </div>
                </div>
              )}

              {(aaStatus === "REJECTED" || aaStatus === "FAILED") && (
                <div className="rounded-2xl border border-red-200 bg-red-50 p-5 flex gap-4 items-start">
                  <AlertCircle className="text-red-600 shrink-0" size={28} />
                  <div>
                    <h4 className="font-semibold text-base sm:text-lg text-[#1D3557]">
                      {aaStatus === "REJECTED" ? "Consent not approved" : "We couldn't fetch your statement"}
                    </h4>
                    <p className="text-sm sm:text-base text-gray-600 mt-1">{statusMessage}</p>
                    <p className="text-sm text-gray-500 mt-2">
                      {aaStatus === "REJECTED"
                        ? "Sharing your statement through Account Aggregator is required to continue. Please try again and approve the consent."
                        : fallbackAllowed === 1
                          ? "You can try again, or upload your bank statement PDF instead."
                          : "Please try again in a moment."}
                    </p>
                  </div>
                </div>
              )}

              <div className="mt-8 w-full flex flex-col gap-3">
                {aaStatus === "PROCESSING" ? (
                  !isWaiting && <Button className="w-full bg-[#2D5F7A]" title="Check Again" onClick={checkAgain} />
                ) : (
                  <Button
                    className="w-full bg-[#2D5F7A]"
                    title={aaStatus === "NOT_STARTED" ? "Connect Bank Account" : "Try Again"}
                    onClick={startAccountAggregator}
                    loading={loading}
                  />
                )}

                {fallbackAllowed === 1 && aaStatus !== "COMPLETED" && aaStatus !== "PROCESSING" && (
                  <button
                    type="button"
                    onClick={uploadManually}
                    style={{ background: "transparent", border: "none", padding: 0, fontWeight: 500 }}
                    className="text-[#2D5F7A] underline underline-offset-4 text-sm sm:text-base self-center hover:text-[#1D3557]"
                  >
                    Upload bank statement PDF instead
                  </button>
                )}
              </div>

              <div className="mt-8 flex gap-3 items-start text-xs sm:text-sm text-gray-500">
                <ShieldCheck className="text-green-600 shrink-0" size={18} />
                <span>Account Aggregator is an RBI-regulated framework. Your bank shares a read-only statement with your consent; we never see your net-banking login.</span>
              </div>
            </>
          )}
        </FormWrapper2>
      </div>
    </BoxWrapper>
  );
}

export default AccountAggregator;
